import type * as types from "../../../types/types";
import ProductionSegments from "./ProductionSegments/ProductionSegments";
import { AlternateTimelineProductionSegments } from "./ProductionSegments/AlternateTimelineProductionSegments";
import {
  getIsSafari,
  ROW_HEIGHT,
  ROW_PADDING,
  SHRINK_TIMELINE_HEIGHT,
} from "../../../constants/constants";
import * as d3 from "d3";
import {
  useTimeAxisContext,
  useFocusRange,
  useIsSerialProcess,
  useIsSingleProcessPage,
  useGetPageFromPath,
  useIsAdmin,
} from "utils/customHooks/genericHooks";
import { useSelector } from "store/useSelector";
import RetrotagSegmentSelector from "./RetrotagPopper/RetrotagSegmentSelector";
import { useIsRetrotagPopperOpen } from "utils/customHooks/retrotagSliceSelectors";
import { CountSegments } from "./SkuOperatorTimeline/NetCountSegments";
import { NetCountTooltip } from "./SkuOperatorTimeline/NetCountCanvas";
import { RejectCountSegments } from "./SkuOperatorTimeline/RejectCountSegments";
import { SkuSegments } from "./SkuOperatorTimeline/SkuSegments";
import { TeamSizeSegments } from "./SkuOperatorTimeline/TeamSizeSegments";
import { OperatorSegments } from "./SkuOperatorTimeline/OperatorSegments";
import {
  GrossTicksSegments,
  RejectTicksSegments,
  NetTicksSegments,
  SpeedGainLossTicksSegments,
} from "./SkuOperatorTimeline/GrossNetRejectDebugTicksSegments";
import { TicksTooltip } from "./SkuOperatorTimeline/TicksTooltip";
import { MouseMoveHandler } from "./ProductionSegments/MouseMoveHandler";
import {
  useDataSourceGroups,
  useHighlightedTags,
  useIsTimelineExpanded,
} from "../HubletView/SingleTimeline/singleTimelineStore";
import { useHighlightOverlappingSegments, useIsDarkMode } from "store/atoms";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useMemo, useRef } from "react";
import { ObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { CheapCanvasProductionSegments } from "./CheapCanvasProductionSegments";
import { ProductionSubSegments } from "./ProductionSegments/ProductionSubSegments";
import styled from "styled-components/macro";
import { darkGrey, lighterGrey } from "constants/colors";
import {
  useNumVisibleSubTimelines,
  useTimelinesStore,
  VisibleSubTimelines,
} from "./TimelineCardControls/timelinesStore";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useInterval } from "react-use";
import { useForceRerenderOnIntervalIfCurrentRange } from "components/DataGetter/useForceRerenderOnIntervalIfCurrentRange";

type TimelineSubsegments = {
  // for each key of VisibleSubTimelines, a component to render
  timelineKey: VisibleSubTimelines;
  component: React.FC<{ processId: string; order: number; width: number }>;
}[];
const TIMELINE_SUBSEGMENTS: TimelineSubsegments = [
  { timelineKey: "teamSize", component: TeamSizeSegments },
  { timelineKey: "operator", component: OperatorSegments },
  { timelineKey: "sku", component: SkuSegments },
  { timelineKey: "count", component: CountSegments },
  { timelineKey: "rejectCount", component: RejectCountSegments },
  {
    timelineKey: "grossRejectNetTicks",
    component: GrossRejectNetTicksWithSpeed,
  },
];

function GrossRejectNetTicksWithSpeed({
  width,
  processId,
}: {
  width: number;
  processId: string;
}) {
  const numVisibleSubTimelines = useNumVisibleSubTimelines();
  const { visibleSubTimelines } = useTimelinesStore();
  const offset = visibleSubTimelines.speedGainLoss ? -1 : 0;
  // Adding +1 to each order to move segments down by 1 row to fix overlap issue
  return (
    <>
      <GrossTicksSegments
        width={width}
        order={numVisibleSubTimelines - 4 + offset + 1}
      />
      <RejectTicksSegments
        width={width}
        order={numVisibleSubTimelines - 3 + offset + 1}
      />
      <NetTicksSegments
        width={width}
        order={numVisibleSubTimelines - 2 + offset + 1}
      />
      {visibleSubTimelines.speedGainLoss && (
        <SpeedGainLossTicksSegments
          width={width}
          order={numVisibleSubTimelines - 1 + offset + 1}
          processId={processId}
        />
      )}
      <ErrorBoundary small={true}>
        <TicksTooltip
          order={numVisibleSubTimelines - 1 + offset + (getIsSafari() ? 1 : 0)}
        />
      </ErrorBoundary>
    </>
  );
}

interface TimelineProps {
  process: { name: string; id: string };
  productionSegments: types.ProductionSegmentType[];
  offset?: number;
  timelineIdx: number;
  style?: React.CSSProperties;
  width: number;
}

const Timeline = ({
  offset,
  process,
  productionSegments,
  timelineIdx,
  style = {},
  width,
}: TimelineProps) => {
  const showRetrotagPopper = useShowRetrotagPopper(process.id);

  const isSerialProcess = useIsSerialProcess();
  const isSingleProcessPage = useIsSingleProcessPage();
  const selectedProcessIdsRef = useSelector(
    state => state.legacy.selectedProcessIdsRef
  );
  const isProcessSelected = selectedProcessIdsRef.current.includes(process.id);
  const isSelectedTimeline =
    !isSerialProcess || isSingleProcessPage || isProcessSelected;

  const { visibleSubTimelines } = useTimelinesStore();

  const { isHubletPage } = useGetPageFromPath();
  const showSkuAndOperatorTimeline = isHubletPage;
  const className = `Timeline-${process.id}`;
  const isAdminUser = useIsAdmin();
  const { selectedDataSourceGroups } = useDataSourceGroups();
  const [shouldHighlightOverlappingSegments] =
    useHighlightOverlappingSegments();
  const isStorybook = useIsStorybook();
  const { flightEnableAlternateTimeline, flightUseVirtualTimelineSegments } =
    useFlagsWithDefaults();
  const [isDarkMode] = useIsDarkMode();
  const [highlightedTags] = useHighlightedTags();
  const areAnyTagsHighlighted = highlightedTags.length > 0;
  const areAnyDataSourceGroupsSelected = selectedDataSourceGroups.length > 0;
  const shrinkTimelines = areAnyDataSourceGroupsSelected;
  const [isTimelineExpanded] = useIsTimelineExpanded();
  const rerenderKey = useForceRerenderOnIntervalIfCurrentRange();
  return (
    <g
      transform={`translate(0, ${offset})`}
      data-component-name={"Timeline"}
      className={className}
      width={width}
      style={style}
    >
      {timelineIdx === 0 && (
        <ObserveComponentLoadTime
          name="Timeline"
          loading={Boolean(productionSegments.length <= 1)}
        />
      )}

      {flightUseVirtualTimelineSegments ? (
        <CheapCanvasProductionSegments
          key={rerenderKey}
          processId={process.id}
          segments={productionSegments}
          timelineIdx={timelineIdx}
          height={shrinkTimelines ? SHRINK_TIMELINE_HEIGHT : ROW_HEIGHT}
          width={width}
        />
      ) : (
        <ProductionSegments
          processId={process.id}
          segments={productionSegments}
          timelineIdx={timelineIdx}
          height={shrinkTimelines ? SHRINK_TIMELINE_HEIGHT : ROW_HEIGHT}
        />
      )}
      {(isAdminUser || flightEnableAlternateTimeline) &&
        areAnyDataSourceGroupsSelected && (
          <AlternateTimelineProductionSegments
            processId={process.id}
            timelineIdx={timelineIdx}
          />
        )}
      {(isAdminUser || isStorybook) && shouldHighlightOverlappingSegments && (
        <OverlappingSegments productionSegments={productionSegments} />
      )}
      {isHubletPage ? null : (
        <TagHighlightBackdropStyles
          $isDarkMode={isDarkMode}
          $show={areAnyTagsHighlighted}
        />
      )}
      {flightUseVirtualTimelineSegments && areAnyTagsHighlighted ? (
        <>
          <svg style={{ overflow: "visible", pointerEvents: "none" }}>
            <ProductionSubSegments process={process} />
          </svg>
        </>
      ) : null}
      {showRetrotagPopper && <RetrotagSegmentSelector />}
      {isSelectedTimeline ? null : <OutOfFocusPath />}
      {showSkuAndOperatorTimeline ? (
        <>
          {isTimelineExpanded ? (
            <SubTimelinesStyles>
              {/* Add tooltips for count and reject count segments */}
              <ErrorBoundary small={true}>
                {visibleSubTimelines.count ||
                visibleSubTimelines.rejectCount ? (
                  <NetCountTooltip order={0} />
                ) : null}
              </ErrorBoundary>

              {TIMELINE_SUBSEGMENTS.filter(
                subSegment => visibleSubTimelines[subSegment.timelineKey]
              ).map((subSegment, idx) => {
                return (
                  <subSegment.component
                    key={idx}
                    processId={process.id}
                    order={idx}
                    width={width}
                    {...(subSegment.timelineKey === "grossRejectNetTicks" && {
                      processId: process.id,
                    })}
                  />
                );
              })}
            </SubTimelinesStyles>
          ) : null}
        </>
      ) : null}
      <MouseMoveHandler
        timelineSelector={`.${className}`}
        {...{ timelineIdx }}
      />
    </g>
  );
};
const SubTimelinesStyles = styled.g``;
function useShowRetrotagPopper(processId: string) {
  const retrotagPopperProcessId = useSelector(
    state => state.retrotag.retrotagProcessId
  );
  const isRetrotagPopperOpen = useIsRetrotagPopperOpen(processId);

  return useMemo(
    () => isRetrotagPopperOpen && retrotagPopperProcessId === processId,
    [isRetrotagPopperOpen, processId, retrotagPopperProcessId]
  );
}
function OverlappingSegments({
  productionSegments,
}: {
  productionSegments: types.ProductionSegmentType[];
}) {
  const { xLeft, xRight, scaleTime } = useTimeAxisContext();
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);

  const overlapSegments = productionSegments.reduce(
    (acc, segment, idx) => {
      // for each segment, loop over all adjacent segments and highlight them if they overlap
      const otherSegments = [
        ...productionSegments.slice(0, idx),
        ...productionSegments.slice(idx + 1),
      ];
      otherSegments.forEach(other => {
        if (!segment.range?.ge || !other.range?.ge) {
          return;
        }

        const segLt = segment.range.lt ?? now.current;
        const otherLt = other.range.lt ?? now.current;

        const doesOverlapLt =
          // overlap on right side (segment lt overlaps with other)
          segLt > other.range.ge && segLt <= otherLt;
        if (doesOverlapLt) {
          // create a new segment from the overlapping range
          acc = [
            ...acc,
            {
              range: {
                ge: other.range.ge,
                lt: segLt,
              },
            },
          ];
        }
        // no need to check ge since we'll check lt on the next segment
      });
      return acc;
    },
    [] as { range: types.TimeRangeFinite }[]
  );

  return (
    <>
      <pattern
        id="diagonalHatch"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
      >
        <path
          d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"
          style={{ stroke: "white", strokeWidth: 1 }}
        ></path>
      </pattern>
      {overlapSegments.map((segment, idx) => {
        const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : xLeft;
        const x1 = scaleTime(segment.range.lt as number) || 0;
        const rectRight = Math.min(x1, xRight);
        const rectLeft = Math.max(x0, xLeft);
        const rectWidth = rectRight - rectLeft;

        const height = ROW_HEIGHT / 4;

        return (
          <g key={idx}>
            <rect
              className="overlapping-segment"
              x={rectLeft}
              y={-height / 2 + ((idx * 3) % height)} // stagger for visibility
              width={rectWidth}
              height={height}
              fill={"url(#diagonalHatch)"}
              style={{
                pointerEvents: "none",
                zIndex: 1000,
                stroke: "black",
              }}
            />
          </g>
        );
      })}
    </>
  );
}

export default Timeline;

function OutOfFocusPath() {
  const focusRange = useFocusRange();
  const { xLeft, xRight, scaleTime } = useTimeAxisContext();

  if (!focusRange) {
    return null;
  }

  const xLeftFocus = Math.max(scaleTime(focusRange.ge) || 0, xLeft);
  const xRightFocus = Math.min(scaleTime(focusRange.lt) || 0, xRight);

  const top = -ROW_PADDING / 2;
  const bottom = ROW_HEIGHT + ROW_PADDING / 2;

  const pointsTimeline: [number, number][] = [
    [xLeftFocus, top], // top left
    [xRightFocus, top], // top right
    [xRightFocus, bottom], // bottom right
    [xLeftFocus, bottom], // bottom left
  ];
  const areaPath = d3.line()(pointsTimeline);

  return (
    <path
      className="outOfFocusPath outOfFocusPathTimeline"
      d={areaPath ? areaPath : ""}
    />
  );
}

const TagHighlightBackdropStyles = styled.foreignObject<{
  $isDarkMode: boolean;
  $show: boolean;
}>`
  background: ${({ $isDarkMode }) => ($isDarkMode ? darkGrey : lighterGrey)};
  transform: translateY(44px);
  height: 24px;
  width: 100%;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.3s;
  transition-delay: ${({ $show }) => ($show ? "0.3s" : "0s")};
`;
